import type { AccountType } from './account.js';
import type { PortalStrategyNav } from '../lib/portalStrategyNav.js';
import type { IsoTimestamp } from './common.js';

// CLIENT PORTAL — the portal identity.
//
// ⚠️⚠️ PORTAL USERS ARE NOT IN `users`, AND THIS IS THE WHOLE DESIGN.
// Staff live in `users` (domain-gated, role admin|staff). Clients live here, in
// their own collection, behind their own guard. A `role: 'client'` on the staff
// user doc was considered and rejected: it would turn every existing
// `role === 'admin'` / `role !== 'admin'` check in the codebase into a place
// someone can forget to exclude a client, and there are a lot of them. Two
// collections means a client cannot accidentally satisfy a staff check at all —
// there is no shape they could hold that would.
//
// The failure this guards against is commercially severe rather than
// theoretical: competing dealerships of the same marque in the same city, and
// franchisees who would like to see each other's cost per lead. One missed
// scope check leaks a competitor's numbers.

/**
 * Per-feature access, from day one. Scott: *"not all users will get reports."*
 * A portal user with an empty list can sign in and see the names of the accounts
 * they are attached to, and nothing else — which is a legitimate state, not a
 * broken one.
 */
export type PortalPermission = 'reporting' | 'assets' | 'previews' | 'social' | 'kyckconnect_setup' | 'kyckconnect_contact';

export const PORTAL_PERMISSIONS: readonly PortalPermission[] = [
  'reporting',
  'assets',
  'previews',
  'social',
  'kyckconnect_setup',
  'kyckconnect_contact',
];

export const PORTAL_PERMISSION_LABELS: Record<PortalPermission, string> = {
  reporting: 'Reports',
  assets: 'Asset management',
  previews: 'Campaign previews',
  social: 'KyckSocial (posting and inbox)',
  kyckconnect_setup: 'KyckConnect setup',
  kyckconnect_contact: 'Call and text leads',
};

/**
 * Whether the portal actually has a surface behind each permission yet — what
 * the invite and edit dialogs mark "(not built yet)".
 *
 * ⚠️ A `Record` over the permission type, not a list of the unbuilt ones: a new
 * permission added to `PortalPermission` fails to compile until somebody states
 * whether it is built, so the label cannot quietly drift from PORTAL_PERMISSIONS
 * (the old rule was "anything that isn't reporting", which went false the day
 * assets shipped).
 * ⚠️ `assets` is BUILT (the /portal/assets pages + API are live), and its NAV item
 * went live for every portal user on 2026-10-09 — but the nav's status is its own
 * field in portalNav.ts and is deliberately not read from here.
 */
export const PORTAL_PERMISSION_BUILT: Record<PortalPermission, boolean> = {
  reporting: true,
  assets: true,
  previews: false,
  social: true,
  // KyckConnect setup (Phase 4): lets the person change the club-editable setup steps. Without it the Setup page is read-only.
  kyckconnect_setup: true,
  // Call and text leads (Leads centre v2): the Call and Text buttons, the composer and the Sam switch on a lead. Without it they are hidden and the server answers 403.
  kyckconnect_contact: true,
};

/**
 * `invited` — a record exists, nobody has signed in yet, `uid` is null.
 * `active`  — first sign-in matched the email and bound the uid.
 * `disabled` — access revoked. ⚠️ Resolves to an EMPTY scope, and is rejected
 *              at the guard; it is never merely "hidden from the list".
 */
export type PortalUserStatus = 'invited' | 'active' | 'disabled';

export type PortalUser = {
  id: string;
  /**
   * Firebase Auth uid. NULL until the first successful sign-in binds it.
   * ⚠️ Binding happens exactly once (a CAS in the activation transaction): once
   * an invite is claimed, a second identity presenting the same email is
   * refused rather than silently re-bound.
   */
  uid: string | null;
  /**
   * Lowercased. The invite match key AND the document id, so an invite lookup
   * by email is a doc get rather than a query — see the service header for why
   * that matters on the request path of every staff API call.
   */
  email: string;
  displayName: string;
  /**
   * ⚠️⚠️ REQUIRED link to the existing `contacts` record, and since 2026-09-02 it
   * is also WHERE THE SCOPE COMES FROM. One source of truth for who a client's
   * people are, and now one source of truth for what each of them can see.
   * A contact may exist without portal access; a portal user always points at a
   * contact.
   */
  contactId: string;
  /**
   * The accounts granted. May be locations OR groups/brands — a group grants
   * everything operational beneath it. Expansion happens in the ONE scope
   * resolver (src/lib/portalScope.ts), never at a call site.
   *
   * ⚠️⚠️ DERIVED ON READ FROM THE CONTACT'S `accountLinks[]`, AND NOT STORED.
   * It used to be a snapshot taken at invite time, which is precisely how a
   * contact linked to two locations ended up seeing one: nothing reconciled the
   * copy with the contact, and re-sharing from the second location answered
   * "that email already has portal access" — true, and useless. A field kept as
   * a cache is the same bug preserved, so there is no stored field to disagree
   * with anything.
   *
   * ⚠️ CONSEQUENCE, WHICH STAFF SHOULD KNOW: adding an account link to a contact
   * grants that person portal visibility of that account, and removing one takes
   * it away. The contact edit form says so, and the audit entry for a link change
   * names it as an access change.
   */
  grantedAccountIds: string[];
  /**
   * How many OPERATIONAL accounts (locations / dealerships) that resolves to —
   * a group link counts every location beneath it.
   *
   * ⚠️ Derived alongside `grantedAccountIds` through the same resolver, so the
   * staff list and the invite dialog describe what the client will actually see
   * rather than counting raw links.
   */
  locationCount: number;
  permissions: PortalPermission[];
  status: PortalUserStatus;
  invitedByUid: string;
  invitedByName: string;
  invitedAt: IsoTimestamp;
  activatedAt: IsoTimestamp | null;
  /** Doc-only telemetry (the view-tracking precedent) — never audited. */
  lastLoginAt: IsoTimestamp | null;
  disabledAt: IsoTimestamp | null;
};

/** What the STAFF management UI receives. Identical today; a named type so a
 *  future field that must not reach staff has an obvious place to be omitted. */
export type PortalUserSummary = PortalUser;

/**
 * One account a portal user can see, as the portal shell renders it.
 * ⚠️ NAMES ONLY in Phase 0 — no budget, no spend, no status, nothing that would
 * be a leak if the scope were ever wrong. Phase 1 adds report data behind the
 * `reporting` permission.
 */
export type PortalAccountSummary = {
  id: string;
  name: string;
  accountType: AccountType;
  /** True when this account was granted directly (vs. reached by expanding a group). */
  isGrant: boolean;
  parentAccountId: string | null;
};

/** GET /api/portal/me — everything the Phase 0 shell needs. */
export type PortalMe = {
  email: string;
  displayName: string;
  permissions: PortalPermission[];
  accounts: PortalAccountSummary[];
  /**
   * The brand whose theme the portal chrome wears, or null for kyck's own.
   *
   * ⚠️ On `/me` as well as on the report so the shell is themed on EVERY portal
   * page — the home page and the sign-in-finish page have no report on them, and
   * a client whose report was branded and whose home page was not would read as
   * half-finished. Resolved server-side from the scope, through the same
   * `singleBrandOrNull` the report uses; null (multi-brand or no brand) is the
   * kyck default and is the correct answer, not a missing one.
   */
  brandAccountId: string | null;
  /** The brand's name — the header lockup's fallback when a theme has no logo. */
  brandName: string | null;
  /**
   * Which strategy sections this viewer's nav carries (Round 6 §3), resolved
   * SERVER-SIDE from the scope by `resolveStrategyNav`.
   *
   * ⚠️⚠️ ONE RESOLVED VALUE, NEVER PER-ACCOUNT INDUSTRIES. `PortalAccountSummary`
   * deliberately carries no `industry`: sending the ingredients and branching in
   * the browser would be a second place deciding the same question, and the two
   * would drift the first time the rule changed.
   */
  strategyNav: PortalStrategyNav;
};
